import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { BroadcastCall, BroadcastCallService, broadcastCallStatusView } from 'src/app/shared/services/broadcast-call.service';
import { PaginatedResult } from 'src/app/shared/model/paginated-result';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { backendToBrasiliaIso } from 'src/app/shared/utils/brasilia-date';

/** Grupos de `BroadcastCallController::STATUS_GROUPS` (backend) usados no filtro de segmento. */
export type BroadcastCallStatusSegment = 'all' | 'active' | 'pending' | 'completed' | 'cancelled';

const SEGMENTS: { key: BroadcastCallStatusSegment; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Em andamento' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'completed', label: 'Concluídos' },
  { key: 'cancelled', label: 'Cancelados' },
];

@Component({
  selector: 'app-broadcasts-call',
  templateUrl: './broadcasts-call.component.html',
  styleUrls: ['./broadcasts-call.component.scss'],
})
export class BroadcastsCallComponent implements OnInit, OnDestroy {

  page = 1;
  perPage = 15;
  loading = false;
  result: PaginatedResult<BroadcastCall[]>;
  subscriptions: Subscription[] = [];
  /** A busca da lista em andamento — uma por vez (ver `load()`). */
  private loadRequest: Subscription | null = null;

  readonly segments = SEGMENTS;
  search = '';
  segment: BroadcastCallStatusSegment = 'all';

  constructor(
    private broadcastCallService: BroadcastCallService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.loadRequest?.unsubscribe();
  }

  /**
   * Review do MR !558 (ação 12): uma busca por vez. Trocar segmento/busca/página antes da
   * resposta anterior chegar cancela a anterior — senão a que respondesse por último (às vezes
   * a do filtro ANTIGO) sobrescrevia a lista. `loading` não é desligado num `finalize`: cancelar
   * a busca anterior dispararia o `finalize` dela com a nova ainda no ar.
   */
  load(): void {
    this.loading = true;
    const status = this.segment === 'all' ? undefined : this.segment;
    this.loadRequest?.unsubscribe();
    this.loadRequest = this.broadcastCallService.getAll(this.page, this.perPage, this.search || undefined, status)
      .subscribe({
        next: result => {
          this.loading = false;
          this.result = result;
        },
        error: () => {
          this.loading = false;
          this.toastService.error('Erro ao carregar broadcasts de voz');
        },
      });
  }

  /** Busca pelo nome. Chamada pelo botão/Enter da barra de filtros; sempre volta pra página 1. */
  searchSubmit(): void {
    this.page = 1;
    this.load();
  }

  setSegment(segment: BroadcastCallStatusSegment): void {
    if (this.segment === segment) { return; }
    this.segment = segment;
    this.page = 1;
    this.load();
  }

  get hasFilters(): boolean {
    return !!this.search || this.segment !== 'all';
  }

  clearFilters(): void {
    this.search = '';
    this.segment = 'all';
    this.page = 1;
    this.load();
  }

  pageChanged(page: number): void {
    this.page = page;
    this.load();
  }

  view(id: string): void {
    this.router.navigate(['/broadcasts-call/view', id]);
  }

  confirmCancel(bc: BroadcastCall): void {
    this.confirmationService.show(
      `Deseja realmente cancelar o broadcast "${bc.name}"?`,
      () => this.cancelBroadcast(bc.id)
    );
  }

  pauseBroadcast(bc: BroadcastCall): void {
    this.subscriptions.push(
      this.broadcastCallService.pause(bc.id).subscribe({
        next: () => {
          this.toastService.success('Broadcast pausado com sucesso!');
          this.load();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao pausar broadcast'),
      })
    );
  }

  resumeBroadcast(bc: BroadcastCall): void {
    this.subscriptions.push(
      this.broadcastCallService.resume(bc.id).subscribe({
        next: () => {
          this.toastService.success('Broadcast retomado com sucesso!');
          this.load();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao retomar broadcast'),
      })
    );
  }

  cancelBroadcast(id: string): void {
    this.subscriptions.push(
      this.broadcastCallService.cancel(id).subscribe({
        next: () => {
          this.toastService.success('Broadcast cancelado com sucesso!');
          this.load();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao cancelar broadcast'),
      })
    );
  }

  canPause(bc: BroadcastCall): boolean {
    return bc.status?.status === 'Pendente' || bc.status?.status === 'Processando';
  }

  canResume(bc: BroadcastCall): boolean {
    return bc.status?.status === 'Pausado';
  }

  canCancel(bc: BroadcastCall): boolean {
    return bc.status?.status === 'Pendente' || bc.status?.status === 'Processando' || bc.status?.status === 'Pausado';
  }

  /** Status com o "Aguardando retorno" (07/10/2026). Ver `broadcastCallStatusView`. */
  statusView(broadcast: BroadcastCall): { label: string; cls: string } {
    return broadcastCallStatusView(broadcast);
  }

  /** Converte a cor do status (`warning`/`info`/`success`/`danger`/`secondary`) para o tom
   * do `m8v-status-pill` (`warn`/`cool`/`ok`/`danger`/`muted`). */
  statusTone(broadcast: BroadcastCall): string {
    const cls = this.statusView(broadcast).cls;
    switch (cls) {
      case 'warning': return 'warn';
      case 'info': return 'cool';
      case 'success': return 'ok';
      case 'danger': return 'danger';
      default: return 'muted';
    }
  }

  /** % de atendidos sobre o enviado, para a sublinha do cartão/tabela. `null` sem envios ainda. */
  answeredPercent(bc: BroadcastCall): number | null {
    if (!bc.total_sent) { return null; }
    return Math.round(((bc.total_answered ?? 0) / bc.total_sent) * 100);
  }

  /** Normaliza a data agendada (UTC naive do backend) para render em Brasília. */
  brasiliaIso(value: string | null | undefined): string | null {
    return backendToBrasiliaIso(value);
  }
}
